/**
 * Crypto Rescue Mode — activation Card.
 *
 * Mounted at the bottom of the right column of the store Settings page
 * (Edit Store → General tab) by store-form.tsx. Three states:
 *   - Inactive: red-bordered Card + coin icon + red button.
 *   - Active:   status pill + red deactivate button.
 *   - Bake running: both buttons disabled, progress modal open.
 *
 * Auto-reattaches to an in-progress bake job on mount (page refresh
 * mid-bake re-opens the progress modal with current state).
 *
 * Spec: _bmad-output/planning-artifacts/tech-spec-crypto-rescue-mode.md (sec 3.4)
 */
import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, Loader2, AlertCircle, CheckCircle2, LifeBuoy, Wallet } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { STORE_ROUTES } from '@/config/router';
import { cn } from '@/lib/utils';
import {
  useActivateCryptoRescue,
  useCancelCryptoRescueJob,
  useCryptoRescueJob,
  useDeactivateCryptoRescue,
  useStoreRescueState,
  type CryptoRescueJob,
} from '@/lib/api/cryptoRescue';

type ModalKind = 'activate' | 'deactivate' | 'progress' | null;

interface JobLogEntry {
  ts: string;
  level: 'info' | 'warn' | 'error';
  message: string;
}

/**
 * Renders a bake-log message string with backtick-wrapped names
 * highlighted as inline chips. The bake function wraps store and
 * funnel names with `` `name` `` so they stand out from the surrounding
 * sentence (which often contains opaque IDs from upstream errors).
 */
function renderLogMessage(msg: string): React.ReactNode {
  const parts = msg.split(/(`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('`') && part.endsWith('`') && part.length >= 2) {
      return (
        <span
          key={i}
          className="rounded bg-foreground/10 px-1 font-semibold text-foreground"
        >
          {part.slice(1, -1)}
        </span>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

export function CryptoRescueBox() {
  const { id: storeId, orgSlug } = useParams();
  const { t } = useTranslation(['stores', 'common']);
  const { toast } = useToast();
  const navigate = useNavigate();

  const stateQuery = useStoreRescueState(storeId ?? '', !!storeId);
  const activateMutation = useActivateCryptoRescue();
  const deactivateMutation = useDeactivateCryptoRescue();
  const cancelMutation = useCancelCryptoRescueJob(storeId ?? '');

  const [openModal, setOpenModal] = React.useState<ModalKind>(null);
  const [activeJobId, setActiveJobId] = React.useState<string | null>(null);
  const [reason, setReason] = React.useState('');

  // Adopt an in-progress job into activeJobId so the Card can render the
  // live progress bar. No auto-opening of the modal — the user opens it
  // explicitly via the "View details" button. This way they can dismiss
  // the modal during the bake without losing visibility on the progress.
  React.useEffect(() => {
    const inProgressId = stateQuery.data?.inProgressJob?.id;
    if (inProgressId && !activeJobId) {
      setActiveJobId(inProgressId);
    }
  }, [stateQuery.data?.inProgressJob?.id, activeJobId]);

  // Live job polling — drives both the Card's inline progress and the
  // modal's detail view (react-query dedupes against the modal's hook).
  const liveJobQuery = useCryptoRescueJob(activeJobId);
  const liveJob = liveJobQuery.data;
  const isBakingNow = !!liveJob && (liveJob.status === 'pending' || liveJob.status === 'running');

  // When the bake reaches a terminal state AND no modal is currently
  // open, clear activeJobId so a subsequent activate/deactivate can run.
  // (If the modal IS open, we keep activeJobId so the modal shows the
  // final state — clearing happens on modal close.)
  React.useEffect(() => {
    if (!liveJob) return;
    if (liveJob.status !== 'completed' && liveJob.status !== 'failed') return;
    stateQuery.refetch();
    if (openModal === 'progress') return;
    setActiveJobId(null);
  }, [liveJob?.status, openModal]);

  if (!storeId) return null;

  // Platform-level gate: feature is unavailable when the env-driven
  // fallback isn't configured. Hide the entire Card unless the server
  // EXPLICITLY confirms it's configured — also hides during loading and
  // when an older bundle response is missing the `configured` field.
  if (!stateQuery.data?.configured) return null;

  const isActive = stateQuery.data?.active === true;

  const handleConfirmActivate = async () => {
    try {
      const { jobId } = await activateMutation.mutateAsync({
        storeId,
        reason: reason.trim() || undefined,
      });
      setActiveJobId(jobId);
      setOpenModal('progress');
      setReason('');
    } catch (err) {
      toast({
        title: t('cryptoRescue.toast.error'),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  };

  const handleConfirmDeactivate = async () => {
    try {
      const { jobId } = await deactivateMutation.mutateAsync({ storeId });
      setActiveJobId(jobId);
      setOpenModal('progress');
    } catch (err) {
      toast({
        title: t('cryptoRescue.toast.error'),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  };

  const handleCancelJob = async () => {
    if (!activeJobId) return;
    try {
      await cancelMutation.mutateAsync({ jobId: activeJobId });
      setActiveJobId(null);
      setOpenModal(null);
      stateQuery.refetch();
    } catch (err) {
      toast({
        title: t('cryptoRescue.toast.error'),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  };

  const handleCloseProgress = () => {
    setOpenModal(null);
    // If the bake is still running, leave activeJobId set so the Card
    // keeps polling + showing inline progress. Only clear after a
    // terminal state.
    if (liveJob?.status === 'completed' || liveJob?.status === 'failed') {
      setActiveJobId(null);
    }
  };

  return (
    <>
      <Card className="border-2 border-red-500/70 bg-red-50/30 dark:bg-red-950/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <LifeBuoy className="size-5" />
            {t('cryptoRescue.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isBakingNow && liveJob ? (
            <InlineProgress
              job={liveJob}
              onClickDetails={() => setOpenModal('progress')}
              onCancel={handleCancelJob}
              cancelPending={cancelMutation.isPending}
              t={t}
            />
          ) : isActive ? (
            <>
              <div className="rounded-md bg-red-100 px-3 py-2 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-300">
                <div className="flex items-center gap-2 font-semibold">
                  <LifeBuoy className="size-4" />
                  {t('cryptoRescue.statusActive')}
                </div>
                {stateQuery.data?.activatedAt && (
                  <div className="mt-1 text-xs">
                    {t('cryptoRescue.activatedAt', {
                        date: new Date(stateQuery.data.activatedAt).toLocaleString(),
                    })}
                  </div>
                )}
                {stateQuery.data?.reason && (
                  <div className="mt-1 text-xs italic">
                    {t('cryptoRescue.reasonLabel')}: {stateQuery.data.reason}
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="destructive"
                className="w-full"
                onClick={() => setOpenModal('deactivate')}
              >
                {t('cryptoRescue.button.deactivate')}
              </Button>
            </>
          ) : (
            <>
              <p className="text-xs text-red-700 dark:text-red-400">
                {t('cryptoRescue.disclaimer')}
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate(STORE_ROUTES(orgSlug!, storeId!).INTEGRATION_CONFIG('crypto'))}
              >
                <Wallet className="size-4" />
                {t('cryptoRescue.button.configureWallet')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="w-full"
                onClick={() => setOpenModal('activate')}
              >
                {t('cryptoRescue.button.activate')}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <ActivateModal
        open={openModal === 'activate'}
        onOpenChange={(o) => !o && setOpenModal(null)}
        reason={reason}
        onReasonChange={setReason}
        onConfirm={handleConfirmActivate}
        pending={activateMutation.isPending}
        t={t}
      />

      <DeactivateModal
        open={openModal === 'deactivate'}
        onOpenChange={(o) => !o && setOpenModal(null)}
        onConfirm={handleConfirmDeactivate}
        pending={deactivateMutation.isPending}
        t={t}
      />

      {activeJobId && (
        <ProgressModal
          open={openModal === 'progress'}
          jobId={activeJobId}
          onClose={handleCloseProgress}
          onCancel={handleCancelJob}
          cancelPending={cancelMutation.isPending}
          t={t}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Inline progress inside the Crypto Rescue Card — shown while a bake
// is pending/running. Mirrors the modal's progress section in a compact
// form. "View details" button opens the modal for the full log view.
// ─────────────────────────────────────────────────────────────────────

interface InlineProgressProps {
  job: CryptoRescueJob;
  onClickDetails: () => void;
  onCancel: () => void;
  cancelPending: boolean;
  t: ReturnType<typeof useTranslation>['t'];
}

function InlineProgress({ job, onClickDetails, onCancel, cancelPending, t }: InlineProgressProps) {
  const action = job.action ?? 'activate';
  const total = job.progressTotal ?? 0;
  const current = job.progressCurrent ?? 0;
  const percent = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
  const isSearching = total === 0;
  const titleKey =
    action === 'activate'
      ? 'cryptoRescue.modal.progress.activating'
      : 'cryptoRescue.modal.progress.deactivating';
  const titleDefault =
    action === 'activate'
      ? 'Activating Crypto Rescue Mode…'
      : 'Deactivating Crypto Rescue Mode…';

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-red-800 dark:text-red-300">
        <Loader2 className="size-4 animate-spin" />
        {t(titleKey, { defaultValue: titleDefault })}
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {isSearching
              ? t('cryptoRescue.modal.progress.searching')
              : t('cryptoRescue.modal.progress.counter', { current, total })}
          </span>
          {!isSearching && <span>{percent}%</span>}
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          {isSearching ? (
            <div className="h-full w-full animate-pulse bg-emerald-500/40" />
          ) : (
            <div
              className="h-full bg-emerald-500 transition-all duration-300"
              style={{ width: `${percent}%` }}
            />
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" className="flex-1" onClick={onClickDetails}>
          <Eye className="size-4" />
          {t('cryptoRescue.button.viewDetails')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="text-muted-foreground"
          onClick={onCancel}
          disabled={cancelPending}
        >
          {cancelPending ? <Loader2 className="size-4 animate-spin" /> : t('common:cancel')}
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Activate modal
// ─────────────────────────────────────────────────────────────────────

interface ActivateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reason: string;
  onReasonChange: (value: string) => void;
  onConfirm: () => void;
  pending: boolean;
  t: ReturnType<typeof useTranslation>['t'];
}

function ActivateModal({
  open,
  onOpenChange,
  reason,
  onReasonChange,
  onConfirm,
  pending,
  t,
}: ActivateModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-red-700 dark:text-red-400">
            {t('cryptoRescue.modal.activate.title')}
          </DialogTitle>
          <DialogDescription className="space-y-2 pt-2">
            <span className="block">
              {t('cryptoRescue.modal.activate.body1')}
            </span>
            <span className="block">
              {t('cryptoRescue.modal.activate.body2')}
            </span>
            <span className="block">
              {t('cryptoRescue.modal.activate.body3')}
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-sm font-medium">
            {t('cryptoRescue.reasonLabel')}{' '}
            <span className="text-muted-foreground text-xs font-normal">
              ({t('common:optional')})
            </span>
          </label>
          <Textarea
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder={t('cryptoRescue.reasonPlaceholder')}
            disabled={pending}
            rows={3}
            maxLength={500}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            {t('common:cancel')}
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm} disabled={pending}>
            {pending && <Loader2 className="size-4 animate-spin" />}
            {t('cryptoRescue.modal.activate.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Deactivate modal
// ─────────────────────────────────────────────────────────────────────

interface DeactivateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  pending: boolean;
  t: ReturnType<typeof useTranslation>['t'];
}

function DeactivateModal({ open, onOpenChange, onConfirm, pending, t }: DeactivateModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('cryptoRescue.modal.deactivate.title')}
          </DialogTitle>
          <DialogDescription className="pt-2">
            {t('cryptoRescue.modal.deactivate.body')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            {t('common:cancel')}
          </Button>
          <Button type="button" variant="default" onClick={onConfirm} disabled={pending}>
            {pending && <Loader2 className="size-4 animate-spin" />}
            {t('cryptoRescue.modal.deactivate.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Progress modal — polls getJob every 1.5s
// ─────────────────────────────────────────────────────────────────────

interface ProgressModalProps {
  open: boolean;
  jobId: string;
  onClose: () => void;
  onCancel: () => void;
  cancelPending: boolean;
  t: ReturnType<typeof useTranslation>['t'];
}

function ProgressModal({ open, jobId, onClose, onCancel, cancelPending, t }: ProgressModalProps) {
  const jobQuery = useCryptoRescueJob(open ? jobId : null);
  const job = jobQuery.data as CryptoRescueJob | undefined;

  const isDone =
    job?.status === 'completed' || job?.status === 'failed';

  // Auto-scroll the log container to the bottom as new entries arrive.
  const logRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [job?.logEntries?.length]);

  const action = job?.action ?? 'activate';
  const total = job?.progressTotal ?? 0;
  const current = job?.progressCurrent ?? 0;
  const percent = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;

  const titleKey = isDone
    ? action === 'activate'
      ? 'cryptoRescue.modal.progress.success.activate'
      : 'cryptoRescue.modal.progress.success.deactivate'
    : action === 'activate'
      ? 'cryptoRescue.modal.progress.activating'
      : 'cryptoRescue.modal.progress.deactivating';

  const titleDefault = isDone
    ? action === 'activate'
      ? 'Crypto Rescue Mode is now active'
      : 'Crypto Rescue Mode is now off'
    : action === 'activate'
      ? 'Activating Crypto Rescue Mode…'
      : 'Deactivating Crypto Rescue Mode…';

  // User can close the modal at any time — the bake continues in the
  // background and the Card keeps showing inline progress so the user
  // can re-open the modal whenever they want to see the full log.
  const handleOpenChange = (next: boolean) => {
    if (!next) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {job?.status === 'failed' ? (
              <AlertCircle className="size-5 text-red-600" />
            ) : isDone ? (
              <CheckCircle2 className="size-5 text-emerald-600" />
            ) : (
              <Loader2 className="size-5 animate-spin" />
            )}
            {t(titleKey, { defaultValue: titleDefault })}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
              <span>
                {total === 0 && !isDone
                  ? t('cryptoRescue.modal.progress.searching')
                  : t('cryptoRescue.modal.progress.counter', { current, total })}
              </span>
              {(total > 0 || isDone) && <span>{percent}%</span>}
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              {total === 0 && !isDone ? (
                <div className="h-full w-full animate-pulse bg-emerald-500/40" />
              ) : (
                <div
                  className={cn(
                    'h-full transition-all duration-300',
                    job?.status === 'failed' ? 'bg-red-500' : 'bg-emerald-500',
                  )}
                  style={{ width: `${percent}%` }}
                />
              )}
            </div>
          </div>

          <div
            ref={logRef}
            className="h-48 overflow-y-auto rounded-md border border-border bg-muted/30 p-2 font-mono text-xs"
          >
            {(job?.logEntries as JobLogEntry[] | undefined)?.length ? (
              (job!.logEntries as JobLogEntry[]).map((entry, i) => (
                <div
                  key={i}
                  className={cn(
                    'py-0.5',
                    entry.level === 'error' && 'text-red-600',
                    entry.level === 'warn' && 'text-amber-600',
                  )}
                >
                  <span className="text-muted-foreground">
                    {new Date(entry.ts).toLocaleTimeString()}{' '}
                  </span>
                  {renderLogMessage(entry.message)}
                </div>
              ))
            ) : (
              <div className="text-muted-foreground italic">
                {t('cryptoRescue.modal.progress.waiting')}
              </div>
            )}
          </div>

          {job?.status === 'failed' && job.error && (
            <div className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800 dark:border-red-700 dark:bg-red-950/30 dark:text-red-300">
              {t('cryptoRescue.modal.progress.error')}
              <div className="mt-1 font-mono">{job.error}</div>
            </div>
          )}
        </div>

        <DialogFooter>
          {!isDone && (
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={cancelPending}
              className="text-muted-foreground"
            >
              {cancelPending && <Loader2 className="size-4 animate-spin" />}
              {t('common:cancel')}
            </Button>
          )}
          <Button type="button" onClick={onClose}>
            {t('common:close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
